// css =>
import "../../BaseStructure.css";
import Styled from "./ProductInfo.module.css";

// icons =>
import SaveIcon from "../../Assets/Icons/big-save.png";
import NotSaveIcon from "../../Assets/Icons/small-save.png";

// componenets =>
import Header from "../../Components/header/header.comp";
import MenuBar from "../../Components/menuBar/menuBar.comp";
import SpinnerBar from "../../Components/spinnerBar/spinnerBar.comp";
import MobileSubMenu from "../../Components/mobileSubMenu/mobileSubMenu.comp";
import MobileMenuBar from "../../Components/mobileMenuBar/mobileMenuBar.comp";
import DestopSubMenu from "../../Components/desktopSubMenu/desktopSubMenu.comp";
import SpecificationTable from "../../Components/specificationTable/specificationTable.comp";

// pic =>
import NotImage from "../../Assets/Pictures/No_Image.png";

// react =>
import { useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Services =>
import { addProductToCart, addQuantity, minimiseQuantity, handleScroll } from "./ProductInfoService";

// context =>
import { useMessage } from "../../Contexts/MessageBoxContext";

// type =>
import { PRODUCT, PRODUCT_PICTURE } from "./ProductInfoType";

// API =>
import backEnd from "../../services/Api";

// utils =>
import { formatPrice } from "../../utils/ChangePriceFormat";
import { LocalCSRFReceiver } from "../../utils/GetLocalCSRF";

// hooks =>
import { useUserStore } from "../../hooks/useUserStore";

import { motion, AnimatePresence } from "framer-motion";

function ProductInfo() {
    const selectColorMobileMenuRef = useRef<HTMLDivElement>(null);
    const selectVariantsMobileMenuRef = useRef<HTMLDivElement>(null);

    const selectColorDesktopMenuRef = useRef<HTMLDivElement>(null);
    const selectVariantsDesktopMenuRef = useRef<HTMLDivElement>(null);

    const params = useParams();
    const galleryRef = useRef<HTMLDivElement>(null);

    const descriptionSectionRef = useRef<HTMLDivElement>(null);
    const selectPropertiesSectionRef = useRef<HTMLDivElement>(null);
    const productSpecificationSectionRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();
    const { showMessage } = useMessage();

    const [menuPathIndex, setMenuPathIndex] = useState<number>(1);
    const [selectedColorId, setSelectedColorId] = useState<number>(0);
    const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
    const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);

    const [selectedColorMenuOption, setSelectedColorMenuOption] = useState<number>(0);
    const [selectedVariantsMenuOption, setSelectedVariantsMenuOption] = useState<number>(0);

    const [addedToCart, setAddedToCart] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isInWishlist, setIsInWishlist] = useState<boolean>(false);
    const [productTotalPrice, setProductTotalPrice] = useState<string>("");
    const [productDetail, setProductDetail] = useState<PRODUCT | null>(null);
    const [alreadyPictureSlideNumber, setAlreadyPictureSlideNumber] = useState<number>(0);
    const [productPictures, setProductPictures] = useState<PRODUCT_PICTURE[] | null>(null);

    const [selectedPicIndex, setSelectedPicIndex] = useState<number | null>(null);
    const selectedPicture =
    selectedPicIndex !== null
        ? productPictures?.[selectedPicIndex]
        : null;

    useEffect(() => {
        document.title = "اطلاعات محصول | ویدوک";

        if (!params.productId) {
            navigate("/show-products");
        }

        backEnd.get(`/products/get-product-Detail/${ params.productId }/`).then(( result ) => {
            setProductDetail(result.data);
            setProductPictures(result.data.pictures);

            result.data && result.data.variants.length > 0 ? (
                setProductTotalPrice(result.data.variants[0].price)
            ) : (
                setProductTotalPrice(result.data.price)
            )

            setSelectedColorId(result.data.colors[0].id);
            setSelectedVariantId(result.data?.variants[0]?.id);

            setAlreadyPictureSlideNumber(
                result.data.pictures.length > 0 ? Math.floor(result.data.pictures.length / 2) : 0
            )
            setIsLoading(false);
        }).catch(( error ) => {
            let message = '';

            if (error.response) {
                switch (error.response.status) {
                    case 404:
                        message = 'محصول مورد نظر یافت نشد';
                        break;
                    case 500:
                        message = 'خطای سرور. لطفاً بعداً تلاش کنید';
                        break;
                    default:
                        message = 'خطا در دریافت اطلاعات محصول';
                }
            } else if (error.request) {
                message = 'خطا در برقراری ارتباط با سرور';
            } else {
                message = 'خطای غیرمنتظره رخ داد';
            }

            setIsLoading(false);
            showMessage("error", message);
        });

        checkWishlist();

    }, [params.productId, navigate]);

    const handleAddToCart = async () => {
        setIsLoading(true);

        const result = await addProductToCart({
            productId: Number( params.productId ),
            quantity: selectedQuantity,
            colorId: Number( selectedColorId ),
            variantId: Number( selectedVariantId ) || null,
        });

        if (result.success){
            setIsLoading(false);
            setAddedToCart(true);

            setTimeout(() => {
                setAddedToCart(false);
            }, 5000);

            return showMessage("success", result.message);
        }

        showMessage("error", result.message);
        setIsLoading(false);
    }

    const handleAddQuantity = () => {
        setSelectedQuantity(addQuantity(selectedQuantity));
    }

    const handleMinimiseQuantity = () => {
        if ( selectedQuantity > 1 ) {
            return setSelectedQuantity(minimiseQuantity(selectedQuantity));
        }

        showMessage("error", "مقدار انتخاب شده نمیتواند کمتر از ۱ باشد!");
    }

    const checkWishlist = async () => {
        if (useUserStore.getState().user?.id) {
            try {
                const response = await backEnd.get(`/products/wishlist/check/`, {
                    params: { product_id: params.productId }
                });
    
                setIsInWishlist(response.data.in_wishlist);
            } catch (error: any) {
                showMessage("error", "خطا در چک کردن wishlist:");
            }
        }
    };

    const handleAddToWishlist = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {

            const csrf = await LocalCSRFReceiver();

            const response = await backEnd.post('products/wishlist/add/',
                { product_id: params.productId },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrf,
                    }
                }
            );

            if (response.status === 200 || response.status === 201) {
                setIsInWishlist(true);
                showMessage("success", "محصول به علاقه‌مندی‌ها اضافه شد");
            }
        } catch (error: any) {
            let errorMessage = "خطای غیر منتظره ای رخ داد!";

            if (error.response) {
                switch (error.response.status) {
                    case 400:
                        errorMessage = "اطلاعات ارسالی نامعتبر است";
                        break;
                    case 401:
                    case 403:
                        useUserStore.getState().setUser(null);
                        localStorage.removeItem("user-storage");

                        errorMessage = "لطفا ابتدا وارد حساب کاربری خود شوید!";
                        navigate('/log-in');
                        break;
                    case 404:
                        errorMessage = "محصول مورد نظر یافت نشد";
                        break;
                    case 409:
                        errorMessage = "این محصول قبلاً به علاقه‌مندی‌ها اضافه شده است";
                        setIsInWishlist(true);
                        break;
                    case 500:
                        errorMessage = "خطای سرور. لطفا بعداً تلاش کنید";
                        break;
                    default:
                        errorMessage = "خطایی رخ داد. لطفا دوباره تلاش کنید";
                }
            } else if (error.request) {
                errorMessage = "خطا در برقراری ارتباط با سرور. اتصال اینترنت خود را بررسی کنید";
            } else {
                errorMessage = "خطای غیرمنتظره. لطفا دوباره تلاش کنید";
            }

            showMessage("error", errorMessage);
        }
    };

    const handleRemoveFromWishlist = async (e: React.MouseEvent) => {
        e.stopPropagation();

        try {
            const csrf = await LocalCSRFReceiver();

            const response = await backEnd.post('products/wishlist/remove/',
                { product_id: params.productId },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrf,
                    }
                }
            );

            if (response.status === 200 || response.status === 204) {
                setIsInWishlist(false);
                document.location.reload();
                showMessage("success", "محصول از علاقه‌مندی‌ها حذف شد");
            }
        } catch (error: any) {
            let errorMessage = "خطای غیر منتظره ای رخ داد!";

            if (error.response) {
                switch (error.response.status) {
                    case 400:
                        errorMessage = "اطلاعات ارسالی نامعتبر است";
                        break;
                    case 401:
                        useUserStore.getState().setUser(null);
                        localStorage.removeItem("user-storage");

                        errorMessage = "لطفا ابتدا وارد حساب کاربری خود شوید";
                        navigate('/login');
                        break;
                    case 404:
                        errorMessage = "محصول در لیست علاقه‌مندی‌ها یافت نشد";
                        setIsInWishlist(false);
                        break;
                    case 500:
                        errorMessage = "خطای سرور. لطفا بعداً تلاش کنید";
                        break;
                    default:
                        errorMessage = "خطایی رخ داد. لطفا دوباره تلاش کنید";
                }
            } else if (error.request) {
                errorMessage = "خطا در برقراری ارتباط با سرور. اتصال اینترنت خود را بررسی کنید";
            } else {
                errorMessage = "خطای غیرمنتظره. لطفا دوباره تلاش کنید";
            }

            showMessage("error", errorMessage);
        }
    };

    const handleOpenMobileSelectColorSubMenu = () => {
        if (selectVariantsMobileMenuRef.current) selectVariantsMobileMenuRef.current.style.display = "none";

        if (selectColorMobileMenuRef.current) {
            if (selectColorMobileMenuRef.current.style.display === "flex") {
                selectColorMobileMenuRef.current.style.display = "none";
            } else {
                selectColorMobileMenuRef.current.style.display = 'flex';
            }
        }
    }

    const handleOpenMobileSelectVariantsSubMenu = () => {
        if (selectColorMobileMenuRef.current) selectColorMobileMenuRef.current.style.display = "none";

        if (selectVariantsMobileMenuRef.current) {
            if (selectVariantsMobileMenuRef.current.style.display === "flex") {
                selectVariantsMobileMenuRef.current.style.display = "none";
            } else {
                selectVariantsMobileMenuRef.current.style.display = 'flex';
            }
        }
    }

    const handleOpenDesktopSelectColorSubMenu = () => {
        if (selectVariantsDesktopMenuRef.current) selectVariantsDesktopMenuRef.current.style.display = "none";

        if (selectColorDesktopMenuRef.current) {
            if (selectColorDesktopMenuRef.current.style.display === "flex") {
                selectColorDesktopMenuRef.current.style.display = "none";
            } else {
                selectColorDesktopMenuRef.current.style.display = 'flex';
            }
        }
    }
        
    const handleOpenDesktopSelectVariantsSubMenu = () => {
        if (selectColorDesktopMenuRef.current) selectColorDesktopMenuRef.current.style.display = "none";

        if (selectVariantsDesktopMenuRef.current) {
            if (selectVariantsDesktopMenuRef.current.style.display === "flex") {
                selectVariantsDesktopMenuRef.current.style.display = "none";
            } else {
                selectVariantsDesktopMenuRef.current.style.display = 'flex';
            }
        }
    }

    const handleChangeMenuOption = (pathIndex: number, ref: React.RefObject<HTMLDivElement | null>) => {
        setMenuPathIndex(pathIndex);
        handleScroll(ref);
    }

    return(
        <>
            <Header />
            <MobileMenuBar key={ 0 } isAuthenticated />

            {
                isLoading ? (
                    <SpinnerBar />
                ) : (
                    <>

                        <div className="row" id={ Styled.DesktopProductInfoSection }>
                            <div className="col-12" id={ Styled.DesktopProductNameFrame }>
                                <h1>{ productDetail?.full_name }</h1>
                            </div>

                            <div className="col-12" id={ Styled.ProductInfoMenu }>
                                <div className={`col-2 ${ menuPathIndex === 6 ? Styled.activeDesktop : "" } `} onClick={() => {
                                    handleChangeMenuOption(6, productSpecificationSectionRef);
                                }}>
                                    <h2>طرفداران محصول</h2>
                                </div>
                                <div className={`col-2 ${ menuPathIndex === 5 ? Styled.activeDesktop : "" } `} onClick={() => {
                                    handleChangeMenuOption(5, selectPropertiesSectionRef);
                                }}>
                                    <h2>محصولات مشابه</h2>
                                </div>
                                <div className={`col-2 ${ menuPathIndex === 4 ? Styled.activeDesktop : "" } `} onClick={() => {
                                    handleChangeMenuOption(4, descriptionSectionRef);
                                }}>
                                    <h2>توضیحات محصول</h2>
                                </div>
                                <div className={`col-2 ${ menuPathIndex === 3 ? Styled.activeDesktop : "" } `} onClick={() => {
                                    handleChangeMenuOption(3, selectPropertiesSectionRef);
                                }}>
                                    <h2>رنگبندی</h2>
                                </div>
                                <div className={`col-2 ${ menuPathIndex === 2 ? Styled.activeDesktop : "" } `} onClick={() => {
                                    handleChangeMenuOption(2, productSpecificationSectionRef);
                                }}>
                                    <h2>مشخصات محصول</h2>
                                </div>
                                <div className={`col-2 ${ menuPathIndex === 1 ? Styled.activeDesktop : "" } `} onClick={() => {
                                    handleChangeMenuOption(1, selectPropertiesSectionRef);
                                }}>
                                    <h2>خرید محصول</h2>
                                </div>
                            </div>

                            <div ref={ selectPropertiesSectionRef } className="row" id={ Styled.ProductPropertiesFrame }>
                                <div id={ Styled.ProductPictureFrame }>
                                    <img src={ NotImage } alt="" />
                                    {
                                        productPictures?.map((picture) => {
                                            return <img src={ picture.picture } alt={`picture ${ picture.id }`} />
                                        })
                                    }
                                </div>

                                <div id={ Styled.SelectPropertiesSectionFrame }>
                                    <div onClick={ handleOpenDesktopSelectVariantsSubMenu }>
                                        <p>انتخاب ویژگی :</p>
                                    </div>
                                    <div>
                                        <p>تعداد : { selectedQuantity }</p>
                                    </div>
                                    <div onClick={ handleOpenDesktopSelectColorSubMenu }>
                                        <p>انتخاب رنگ : { productDetail?.colors.find(c => c.id === selectedColorId)?.color_name || "پیش فرض" }</p>
                                    </div>
                                </div>

                                <div id={ Styled.ProductPriceBox }>
                                    <p>قیمت { formatPrice(Number(productTotalPrice) * selectedQuantity) }</p>
                                </div>

                                <div id={ Styled.AddToShoppingCardButtom } style={{ backgroundColor: addedToCart ? "black": "" }} onClick={() => {
                                    handleAddToCart();
                                }}>
                                    {
                                        addedToCart ? (
                                            <h4>با موفقیت به سبد خرید افروده شد</h4>
                                        ) : (
                                            <p>افزودن به سبد خرید</p>
                                        )
                                    }
                                </div>

                            </div>

                            <div ref={ selectColorDesktopMenuRef } className="row" id={ Styled.ColorDesktopSubMenu }>
                                {
                                    productDetail?.colors && productDetail.colors.length > 0 ? (
                                        <DestopSubMenu
                                            key={ 0 }
                                            subMenuOptions={(productDetail?.colors.map(col => col.color_name) || [])} 
                                            onItemClick={(item, index) => {
                                                setSelectedColorId(productDetail.colors.find(c => c.color_name === item)?.id || selectedColorId);
                                                setSelectedColorMenuOption(index);

                                                selectColorDesktopMenuRef.current!.style.display = "none";
                                            }}
                                            selectedOptionIndex={ selectedColorMenuOption }
                                         />
                                    ) : (
                                        <DestopSubMenu
                                            key={ 0 }
                                            subMenuOptions={["این محصول رنگ دیگری ندارد!"]}
                                        />
                                    )
                                }
                            </div>

                            <div ref={ selectVariantsDesktopMenuRef } className="row" id={ Styled.VariantsDesktopSubMenu }>
                                {
                                    productDetail?.variants && productDetail.variants.length > 0 ? (
                                        <DestopSubMenu
                                            key={ 1 }
                                            subMenuOptions={(productDetail.variants.map(col => col.variant + ":" + col.value) || [])} 
                                            onItemClick={(item, index) => {
                                                setSelectedVariantId(productDetail?.variants[index].id || selectedVariantId);
                                                setProductTotalPrice(productDetail?.variants[index].price || productTotalPrice);
                                                setSelectedVariantsMenuOption(index);

                                                selectVariantsDesktopMenuRef.current!.style.display = "none";
                                            }}
                                            selectedOptionIndex={ selectedVariantsMenuOption }
                                         />
                                    ) : (
                                        <DestopSubMenu
                                            key={ 1 }
                                            subMenuOptions={["این محصول ویژگی خاصی ندارد!"]}
                                        />
                                    )
                                }
                            </div>
                        </div>

                        <div className="row">
                            <div className="row" id={ Styled.MobileRightProductInfoBox }>
                                <div className="row" id={ Styled.NameAndSaveSectionFrame }>


                                    <div className="col-6">
                                        {
                                            isInWishlist ? (
                                                <img src={ SaveIcon } alt="Save" onClick={ handleRemoveFromWishlist } />
                                            ) : (
                                                <img src={ NotSaveIcon } alt="Not save" onClick={ handleAddToWishlist } />
                                            )
                                        }
                                    </div>

                                    <div className="col-6">
                                        <h1>{ productDetail?.full_name }</h1>
                                    </div>
                                </div>

                                <div className="row" id={ Styled.ProductPropertiesSectionBox }>
                                    <div
                                        className={`col-3 ${ menuPathIndex === 1 ? Styled.activeMobile : "" } `}
                                        onClick={() => {
                                            handleChangeMenuOption(1, selectPropertiesSectionRef);
                                        }}>
                                        <h4>خرید محصول</h4>
                                    </div>
                                    <div
                                        className={`col-3 ${ menuPathIndex === 2 ? Styled.activeMobile : "" } `}
                                        onClick={() => {
                                            handleChangeMenuOption(2, productSpecificationSectionRef);
                                        }}>
                                        <h4>مشخصات محصول</h4>
                                    </div>
                                    <div
                                        className={`col-3 ${ menuPathIndex === 3 ? Styled.activeMobile : "" } `}

                                        onClick={() => {
                                            handleChangeMenuOption(3, selectPropertiesSectionRef);
                                        }}>
                                        <h4>رنگ بندی</h4>
                                    </div>

                                    <div
                                        className={`col-3 ${ menuPathIndex === 4 ? Styled.activeMobile : "" } `}

                                        onClick={() => {
                                            handleChangeMenuOption(4, descriptionSectionRef);
                                        }}>
                                        <h4>توضیحات محصول</h4>
                                    </div>
                                    <div
                                        className={`col-3 ${ menuPathIndex === 5 ? Styled.activeMobile : "" } `}

                                        onClick={() => {
                                            handleChangeMenuOption(5, selectPropertiesSectionRef);
                                        }}>
                                        <h4>محصولات مشابه</h4>
                                    </div>
                                    <div
                                        className={`col-3 ${ menuPathIndex === 6 ? Styled.activeMobile : "" } `}
                                        onClick={() => {
                                            handleChangeMenuOption(6, productSpecificationSectionRef);
                                        }}>
                                        <h4>طرفداران محصول</h4>
                                    </div>
                                </div>

                                <div ref={ selectPropertiesSectionRef } className="row" id={ Styled.SelectPropertiesSection }>
                                    <div className="col-4" onClick={ handleOpenMobileSelectColorSubMenu }>
                                        <p>انتخاب رنگ: { productDetail?.colors.find(c => c.id === selectedColorId)?.color_name }</p>
                                    </div>

                                    <div className="col-4">
                                        <p>تعداد: { selectedQuantity }</p>
                                    </div>

                                    <div className="col-4" onClick={ handleOpenMobileSelectVariantsSubMenu }>
                                        <p>انتخاب ویژگی: { productDetail?.variants[0]?.variant }</p>
                                    </div>
                                </div>

                                <div ref={ selectColorMobileMenuRef } className={`row ${ Styled.ColorBoxSubMenuFrame } `}>
                                    {
                                        productDetail?.colors && productDetail.colors.length > 0 ? (
                                            <MobileSubMenu
                                                key={ 0 }
                                                subMenuOptions={(productDetail.colors.map(col => col.color_name) || [])}
                                                onItemClick={(item, index) => {
                                                    setSelectedColorId(productDetail?.colors.find(c => c.color_name === item)?.id || selectedColorId);
                                                    setSelectedColorMenuOption(index);

                                                    selectColorMobileMenuRef.current!.style.display = "none";
                                                }}
                                                selectedOptionIndex={ selectedColorMenuOption }
                                            />
                                        ) : (
                                            <MobileSubMenu
                                                key={ 0 }
                                                subMenuOptions={["این محصول رنگ دیگری ندارد!"]}
                                            />
                                        )
                                    }
                                </div>

                                <div ref={ selectVariantsMobileMenuRef } className={`row ${ Styled.VariantBoxSubMenuFrame } `}>
                                    {
                                        productDetail?.variants && productDetail.variants.length > 0 ? (
                                            <MobileSubMenu
                                                key={ 1 }
                                                subMenuOptions={(productDetail.variants.map(col => col.variant + ":" + col.value) || [])}
                                                onItemClick={(item, index) => {
                                                    setSelectedVariantId(productDetail?.variants[index].id || selectedVariantId);
                                                    setProductTotalPrice(productDetail?.variants[index].price || productTotalPrice);
                                                    setSelectedVariantsMenuOption(index);

                                                    selectVariantsMobileMenuRef.current!.style.display = "none";
                                                }}
                                                selectedOptionIndex={ selectedVariantsMenuOption }
                                            />
                                        ) : (
                                            <MobileSubMenu
                                                key={ 1 }
                                                subMenuOptions={["این محصول ویژگی خاصی ندارد!"]}
                                            />
                                        )
                                    }
                                </div>

                                <div className="row" id={ Styled.PaymentSection }>
                                    <div className="col-4">
                                        <p>قیمت: { formatPrice(Number(productTotalPrice) * selectedQuantity) }</p>
                                    </div>
                                    <div className="col-4">
                                        <button style={{ backgroundColor: addedToCart ? "black": "" }} onClick={() => {
                                            handleAddToCart();
                                        }}>
                                            {
                                                addedToCart ? ("با موفقیت به سبد خرید افروده شد") : ("افزودن به سبد خرید")
                                            }
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12" id={ Styled.LeftProductInfoBox }>
                                {
                                    productPictures && productPictures.length > 0 ? (
                                        <div
                                            className="row"
                                            ref={ galleryRef }
                                            id={ Styled.ProductGallery }>
                                            {
                                                productPictures.map((picture: PRODUCT_PICTURE, index: number) => {
                                                    return (
                                                        <motion.div
                                                            key={index}
                                                            layoutId={`image-${index}`}
                                                            className={`col-4 ${Styled.ProductsImagesFrame}`}
                                                            onClick={() => setSelectedPicIndex(index)}
                                                            whileHover={{ scale: 1.03 }}
                                                        >
                                                            <img
                                                                src={picture.picture}
                                                                alt="Product"
                                                            />
                                                        </motion.div>
                                                        
                                                    )
                                                })
                                            }
                                        </div>
                                    ) : (
                                        <div className={`row ${ Styled.NoContentFrame } `}>
                                            <p>هنوز تصویری برای این محصول ثبت نشده است!</p>
                                        </div>
                                    )
                                }
                            </div>
                        </div>

                        <AnimatePresence>
                            {selectedPicture && (
                                <motion.div
                                    className={Styled.ImageOverlay}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    onClick={() => setSelectedPicIndex(null)}
                                >
                                    <motion.img
                                        layoutId={`image-${selectedPicIndex}`}
                                        src={selectedPicture.picture}
                                        alt=""
                                        className={Styled.BigImage}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div ref={ productSpecificationSectionRef } className="row" id={ Styled.ProductSpecificationTableFrame }>
                            <SpecificationTable
                                key={0}
                                specificationList={[
                                    ...(productDetail?.specifications || []),
                                    { specification_title: "تعداد علاقه‌مندان", specification_value: String(productDetail?.product_fan ?? 0) }
                                ]}
                            />
                        </div>

                        <div ref={ descriptionSectionRef } className="row" id={ Styled.ProductDiscriptionFrame }>
                            <div className="col-11">
                                <p>{ productDetail?.about || "محصول توضیحاتی ندارد!" }</p>
                            </div>
                        </div>
        
                        <br /><br /><br />
                    </>
                )
            }

            <br /><br /><br /><br /><br /><br />

            <MenuBar />
        </>
    );
}

export default ProductInfo;
